import { A } from '@adornis/base/env-info.js';
import { type Maybe, type ValueEvent } from '@adornis/base/utilTypes.js';
import { createUserForInstitution } from '@adornis/buchungsheld-db/api/users.js';
import { UserStammdaten } from '@adornis/buchungsheld-db/entities/stammdaten.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { getPluginForScope } from '@adornis/buchungsheld-db/plugins/plugin.js';
import type { BHTranslationDictionary } from '@adornis/buchungsheld-db/translations/translation.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { GenericFieldFormController } from '@adornis/cms/client/generic-field-formcontroller.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { combineLatest, distinctUntilChanged, filter, map, merge, of, switchMap } from 'rxjs';
import '../../../components/bh-stammdaten-form';
import './bh-participation-modify-actions.js';

@customElement('bh-participation-modify-new-user')
export class BHAdminModifyNewUser extends ChemistryLitElement {
  @property({ type: String }) institutionID = new RXController<Maybe<string>>(this, undefined);

  @state() readonly user = new RXController<Maybe<User>>(this, undefined);
  @state() private readonly _userController = new FormController(this, this.user.observable, { success: data => data });

  _savedstammdaten$ = merge(
    of(undefined),
    combineLatest([this.institutionID.observable.pipe(filter(Boolean)), this.user.observable]).pipe(
      switchMap(([institutionID, user]) =>
        user
          ? UserStammdaten.subscribeByUserAndInstitution(user._id, institutionID)(UserStammdaten.allFields)
          : of(undefined),
      ),
    ),
  ).pipe(
    map(it => (it ? it : undefined)),
    distinctUntilChanged(),
  );

  @state() private readonly _stammdaten = new RXController(
    this,
    this._savedstammdaten$.pipe(
      switchMap(savedStammdaten => {
        if (savedStammdaten) return of(savedStammdaten);
        return this.institutionID.observable.pipe(
          filter(Boolean),
          switchMap(async institutionID => {
            return UserStammdaten.createStammdatenForInstitutionID(institutionID, false);
          }),
        );
      }),
    ),
  );

  @state() private readonly _stammdatenController = new GenericFieldFormController(this, this._stammdaten.observable);

  @state() private readonly _translation = new TranslationController<BHTranslationDictionary>(this);

  override render() {
    const institutionID = this.institutionID.value;
    if (!this._userController.document || !this._stammdatenController.document || !institutionID) {
      return html` <x-loading-state></x-loading-state> `;
    }

    return html`
      <x-flex space="md">
        <x-flex space="sm">
          <x-text>Sie wollen doch keinen Nutzer anlegen?</x-text>
          <tr-button-small
            icon="list"
            .action=${() => {
              this.dispatchEvent(new CustomEvent('back'));
            }}
          >
            <x-text>Nutzer auswählen</x-text>
          </tr-button-small>
          <x-flex space="sm">
            <x-input
              placeholder="E-Mail"
              ${this._userController.field('email')}
              @value-picked=${(e: ValueEvent<string>) => {
                if (!this._userController.document) return;
                this._userController.document.username = e.detail.value;
              }}
            ></x-input>
            <x-input
              placeholder=${this._translation.translate('field', 'firstName') + '*'}
              ${this._userController.field('firstName')}
            ></x-input>
            <x-input
              placeholder=${this._translation.translate('field', 'lastName') + '*'}
              ${this._userController.field('lastName')}
            ></x-input>

            <bh-stammdaten-form
              .stammdaten=${this._stammdaten.observable}
              .institutionID=${institutionID}
              .user=${this._userController.document}
              .stammdatenController=${this._stammdatenController}
            ></bh-stammdaten-form>
          </x-flex>
        </x-flex>
        ${this.__actions()}
      </x-flex>
    `;
  }

  private __actions() {
    return html` <bh-participation-modify-actions @save=${() => this._validate()}></bh-participation-modify-actions> `;
  }

  private async _validate() {
    const institutionID = this.institutionID.value;
    if (!institutionID) throw new Error('should not happen');

    // set title only when stammdaten plugin contains title field
    const title = this._stammdatenController.document?.['title'];
    if (this._userController.document && title) this._userController.document.title = title;

    const plugin = getPluginForScope('registration::stammdaten', institutionID);
    const pluginValidationError = plugin.customValidate?.(this._stammdatenController.document);
    if (pluginValidationError) {
      void XSnackbar.show(pluginValidationError);
      return;
    }

    const user = await this._userController.validate();

    if (!user) throw new Error('userData is undefined');
    if (!this._stammdatenController.document) throw new Error('stamdaten doc is undefined');

    if (!user._id) user._id = A.getGloballyUniqueID();

    // validate stammdaten
    this._stammdatenController.document.userID = user._id;
    const stammdaten = await this._stammdatenController.validate();
    if (!stammdaten) throw new Error('stammdaten are undefined');

    // this validation code should be in the registration::stammdaten::sd-plugin, but there it is not possible to access the email-address
    if (plugin.id === 'registration::stammdaten::sd') {
      const { SDInternOccupations } = await import(
        '@adornis/buchungsheld-db/plugins/stammdaten/sd-stammdaten-plugin.js'
      );
      const email = this._userController.document?.email;
      if (!email) throw new Error('should not happen');
      if (
        SDInternOccupations.includes((this._stammdatenController.document as any).occupation) &&
        !email.endsWith('.uni-frankfurt.de')
      ) {
        void XSnackbar.show(
          'Interne Mitarbeiter der Universität Frankfurt müssen sich mit ihrer universitären E-Mail Adresse anmelden (*.uni-frankfurt.de)',
        );
        return;
      }
    }

    // create user
    if (user && stammdaten) {
      const request = createUserForInstitution(institutionID, user, 'user', stammdaten)({ _id: 1 });
      const result = await XDialog.waitFor(request);
      // NOTE: userID could have changed; see comment in 'createUserForInstitution' for more info
      this._stammdatenController.document.userID = result._id;
      this._userController.document!._id = result._id;
      this.dispatchEvent(new CustomEvent('finish', { detail: { value: result._id } }));
    }
  }
}
